import NextLink from 'next/link';
import { Skeleton } from '@savvy-kit/components/skeleton';
import { FadeIn } from '@savvy-kit/foundations/motion/chart-motion';
import { DELAY_STAT_STEP } from '@savvy-kit/foundations/motion/tokens';
import { Text } from '@savvy-kit/foundations/text';
import { cn } from '@savvy-kit/lib/utils';
import { isNil } from 'es-toolkit';

import { useTrackEvent } from '@savvy/deprecated-shared/tracking/use-track-event';

import type { HomeInvestmentRow } from './types';
import { CLIENT_PORTAL_RETRY_DESCRIPTION } from '../common/constants';
import { InstitutionAvatar } from '../common/institution-avatar';
import { PortalCardError } from '../common/portal-card-error';
import { PortalCardLinkAction } from '../common/portal-card-link-action';
import { PortalCard } from '../common/portal-section';
import { isPresent, safeLargeMoney } from '../common/utils';
import { clientPortalNavHref } from '../shell/utils';
import {
  HOME_BOTTOM_ROW_DELAY,
  HOME_HELD_AWAY_MANAGEMENT,
  HOME_INVESTMENT_ROW_ELEMENT_NAME,
  HOME_INVESTMENTS_EMPTY_MESSAGE,
  HOME_INVESTMENTS_ERROR_TITLE,
  HOME_INVESTMENTS_TITLE,
  HOME_INVESTMENTS_VIEW_ACCOUNTS_LABEL,
  homeManagedSubtitle,
} from './constants';

const ACCOUNTS_HREF = clientPortalNavHref('accounts', null);
const SUBTITLE_SEPARATOR = ' · ';

function ViewAccountsAction() {
  if (isNil(ACCOUNTS_HREF)) {
    return null;
  }
  return <PortalCardLinkAction href={ACCOUNTS_HREF} label={HOME_INVESTMENTS_VIEW_ACCOUNTS_LABEL} />;
}

type HomeInvestmentsListProps = {
  readonly loading: boolean;
  readonly failed: boolean;
  readonly onRetry: () => void;
  readonly investments: readonly HomeInvestmentRow[];
  readonly brandName: string;
  readonly delayOffset?: number;
};

const ROW_BODY_CLASS = 'flex min-w-0 flex-1 items-center gap-3 py-3';

const SKELETON_ROWS = ['investment-placeholder-0', 'investment-placeholder-1'];

// The same row geometry as a real row — avatar, two lines, a figure — so the card does not change height
// when the rows arrive.
function InvestmentsSkeleton() {
  return (
    <ul className="px-6 pb-2">
      {SKELETON_ROWS.map((row) => (
        <li key={row} className={cn(ROW_BODY_CLASS, 'border-t border-border-light first:border-t-0')}>
          <Skeleton className="size-7 shrink-0" />
          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            <Skeleton className="h-4 w-40 max-w-full" />
            <Skeleton className="h-3 w-24" />
          </div>
          <Skeleton className="h-5 w-16 shrink-0" />
        </li>
      ))}
    </ul>
  );
}

function NoInvestments() {
  return (
    <div className="px-6 pb-5">
      <Text variant="body-sm" color="secondary">
        {HOME_INVESTMENTS_EMPTY_MESSAGE}
      </Text>
    </div>
  );
}

function investmentSubtitle(investment: HomeInvestmentRow, brandName: string): string {
  if (investment.isManaged) {
    return homeManagedSubtitle(brandName);
  }
  return [HOME_HELD_AWAY_MANAGEMENT, investment.providerName].filter(isPresent).join(SUBTITLE_SEPARATOR);
}

function investmentLinkLabel(investment: HomeInvestmentRow): string {
  return `${investment.name}, ${safeLargeMoney(investment.balance)}`;
}

function InvestmentRowBody({
  investment,
  brandName,
}: {
  readonly investment: HomeInvestmentRow;
  readonly brandName: string;
}) {
  return (
    <>
      <InstitutionAvatar name={investment.providerName ?? investment.name} logoUrl={investment.providerLogoUrl} />
      <div className="flex min-w-0 flex-1 flex-col">
        <Text variant="body-sm" color="dark" className="truncate font-medium">
          {investment.name}
        </Text>
        <Text variant="caption" color="secondary" className="truncate">
          {investmentSubtitle(investment, brandName)}
        </Text>
      </div>
      <span className="shrink-0 font-medium tabular-nums">{safeLargeMoney(investment.balance)}</span>
    </>
  );
}

type InvestmentRowsProps = {
  readonly investments: readonly HomeInvestmentRow[];
  readonly brandName: string;
  readonly delayOffset: number;
};

function InvestmentRows({ investments, brandName, delayOffset }: InvestmentRowsProps) {
  const trackInteraction = useTrackEvent('userInteraction');

  return (
    <ul className="px-6 pb-2">
      {investments.map((investment, index) => (
        <FadeIn
          key={investment.id}
          delay={delayOffset + HOME_BOTTOM_ROW_DELAY + index * DELAY_STAT_STEP}
          className="block"
        >
          <li
            className={cn(
              'flex min-w-0 items-center border-t border-border-light transition-colors first:border-t-0',
              !isNil(investment.href) && 'hover:bg-component-hover',
            )}
          >
            {isNil(investment.href) ? (
              <div className={ROW_BODY_CLASS}>
                <InvestmentRowBody investment={investment} brandName={brandName} />
              </div>
            ) : (
              <NextLink
                href={investment.href}
                className={ROW_BODY_CLASS}
                aria-label={investmentLinkLabel(investment)}
                onClick={() => {
                  trackInteraction({
                    elementName: HOME_INVESTMENT_ROW_ELEMENT_NAME,
                    htmlElement: 'link',
                    interactionType: 'button_click',
                  });
                }}
              >
                <InvestmentRowBody investment={investment} brandName={brandName} />
              </NextLink>
            )}
          </li>
        </FadeIn>
      ))}
    </ul>
  );
}

function InvestmentsBody({
  loading,
  failed,
  onRetry,
  investments,
  brandName,
  delayOffset = 0,
}: HomeInvestmentsListProps) {
  if (failed) {
    return (
      <PortalCardError
        title={HOME_INVESTMENTS_ERROR_TITLE}
        description={CLIENT_PORTAL_RETRY_DESCRIPTION}
        onRetry={onRetry}
      />
    );
  }
  if (loading) {
    return <InvestmentsSkeleton />;
  }
  if (investments.length === 0) {
    return <NoInvestments />;
  }
  return <InvestmentRows investments={investments} brandName={brandName} delayOffset={delayOffset} />;
}

export function HomeInvestmentsList(props: HomeInvestmentsListProps) {
  return (
    <PortalCard
      title={HOME_INVESTMENTS_TITLE}
      action={props.failed ? undefined : <ViewAccountsAction />}
      padded={false}
    >
      <InvestmentsBody {...props} />
    </PortalCard>
  );
}
